import isNil from 'lodash/isNil';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { ApiArbitrage } from 'api';
import { useBalancerTasks } from 'entities/wallet-balancer/hooks/useBalancerTasks';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedSelector } from 'store';
import { Bus } from 'tools';

import { ProjectsList } from '../../../SidebarProjects/ProjectsList';

interface ProjectsProps {
  forceExpandedLayout?: boolean;
  projectDomIdPrefix?: string;
}

const Projects: React.FC<ProjectsProps> = ({
  forceExpandedLayout = false,
  projectDomIdPrefix = 'sidebar-project',
}) => {
  const { pathname } = useLocation();
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);
  const projects = useTypedSelector(store => store.projects.projects);
  const archivedProjects = useTypedSelector(store => store.projects.archivedProjects);

  // State management
  const [opened, setOpened] = useState({ active: true, archive: false });
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [arbitrageIds, setArbitrageIds] = useState<number[]>([]);

  // Refs for cleanup
  const timeoutRefs = useRef<Set<number>>(new Set());
  const isMountedRef = useRef(true);

  // Memoized project data
  const projectData = useMemo(
    () => ({
      active: {
        projects: projects ?? [],
        loading: projects === null,
        type: 'active' as const,
      },
      archive: {
        projects: archivedProjects ?? [],
        loading: archivedProjects === null,
        type: 'archive' as const,
      },
    }),
    [projects, archivedProjects],
  );

  // Balancer tasks hook
  const { data: balancerTasks } = useBalancerTasks(
    useMemo(() => ({ projectId: selectedProjectId, limit: 1000, offset: 0 }), [selectedProjectId]),
    !isNil(selectedProjectId),
  );

  const balancerTasksIds = useMemo(
    () => balancerTasks?.map(task => task.group.id || 0) || [],
    [balancerTasks],
  );

  // Handlers
  const handleOpen = useCallback(
    ({ type, state }: { type: 'active' | 'archive'; state: boolean }) => {
      setOpened(prev => ({
        ...prev,
        [type]: state,
        ...(state ? { [type === 'active' ? 'archive' : 'active']: false } : {}),
      }));
    },
    [],
  );

  const onSelectProject = useCallback((projectId: number): void => {
    setSelectedProjectId(prev => (prev === projectId ? null : projectId));
  }, []);

  const onOpenProject = useCallback((projectId: number) => {
    setSelectedProjectId(projectId);
  }, []);

  // Arbitrage fetching
  const getArbitrages = useCallback(async () => {
    if (!isAdmin || !selectedProjectId || !isMountedRef.current) return;

    try {
      setArbitrageIds([]);
      const { data } = await ApiArbitrage.getArbitrageConfigs({ projectId: selectedProjectId });

      if (isMountedRef.current) {
        setArbitrageIds((data ?? []).map(el => el.id));
      }
    } catch (error) {
      console.error('Failed to fetch arbitrages:', error);
    }
  }, [isAdmin, selectedProjectId]);

  // Route-based project detection and scrolling
  const detectAndScrollToProject = useCallback(() => {
    if (!isMountedRef.current) return;

    const projectMatch = matchPath(`${ROUTE_PATHS.project}/*`, pathname);
    const dexPairMatch = matchPath(ROUTE_PATHS.dexPair, pathname);
    const cexPairMatch = matchPath(ROUTE_PATHS.cexPair, pathname);
    const solPairMatch = matchPath(ROUTE_PATHS.solPair, pathname);
    const arbitrageMatch = matchPath(ROUTE_PATHS.arbitrage, pathname);
    const balancerMatch = matchPath(ROUTE_PATHS.balancer, pathname);
    const balancerCreateMatch = matchPath(ROUTE_PATHS.balancerCreate, pathname);

    let projectId: string | undefined;

    if (projectMatch) {
      projectId = projectMatch.params?.id;
    } else if (dexPairMatch) {
      projectId = dexPairMatch.params?.projectId;
    } else if (cexPairMatch) {
      projectId = cexPairMatch.params?.projectId;
    } else if (solPairMatch) {
      projectId = solPairMatch.params?.projectId;
    } else if (arbitrageMatch) {
      projectId = arbitrageMatch.params?.projectId;
    } else if (balancerMatch) {
      projectId = balancerMatch.params?.projectId;
    } else if (balancerCreateMatch) {
      projectId = balancerCreateMatch.params?.projectId;
    }

    if (projectId && !isNaN(Number(projectId))) {
      const numericProjectId = Number(projectId);
      onOpenProject(numericProjectId);

      // Determine which list should be open based on project data
      const isInActive = projectData.active.projects.some(p => p.id === numericProjectId);
      const isInArchive = projectData.archive.projects.some(p => p.id === numericProjectId);

      if (isInActive) {
        setOpened({ active: true, archive: false });
      } else if (isInArchive) {
        setOpened({ active: false, archive: true });
      }

      // Scroll to project with proper cleanup
      const timeoutId = window.setTimeout(() => {
        if (!isMountedRef.current) return;

        const projectElement = document?.getElementById(`${projectDomIdPrefix}-${projectId}`);
        if (projectElement) {
          projectElement.scrollIntoView({ block: 'start' });
        }
        timeoutRefs.current.delete(timeoutId);
      }, 300);

      timeoutRefs.current.add(timeoutId);
    }
  }, [pathname, projectData, onOpenProject, projectDomIdPrefix]);

  // Effects
  useEffect(() => {
    getArbitrages();
  }, [getArbitrages]);

  useEffect(() => {
    const handleDeleteArbitrage = () => {
      if (isMountedRef.current) {
        getArbitrages();
      }
    };

    Bus.on('delete-arbitrage', handleDeleteArbitrage);
    return () => Bus.off('delete-arbitrage', handleDeleteArbitrage);
  }, [getArbitrages]);

  useEffect(() => {
    detectAndScrollToProject();
  }, [detectAndScrollToProject]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      timeoutRefs.current.forEach(timeout => window.clearTimeout(timeout));
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeoutRefs.current.clear();
    };
  }, []);

  return (
    <>
      <ProjectsList
        forceExpandedLayout={forceExpandedLayout}
        projectDomIdPrefix={projectDomIdPrefix}
        listOpen={opened.active}
        setListOpen={handleOpen}
        loading={projectData.active.loading}
        type="active"
        projects={projectData.active.projects}
        arbitrageIds={arbitrageIds}
        balancerTasksIds={balancerTasksIds}
        getArbitrages={getArbitrages}
        onSelectProject={onSelectProject}
        onOpenProject={onOpenProject}
        selectedProjectId={selectedProjectId}
      />
      {isAdmin && (
        <ProjectsList
          forceExpandedLayout={forceExpandedLayout}
          projectDomIdPrefix={projectDomIdPrefix}
          listOpen={opened.archive}
          setListOpen={handleOpen}
          loading={projectData.archive.loading}
          type="archive"
          projects={projectData.archive.projects}
          arbitrageIds={arbitrageIds}
          balancerTasksIds={balancerTasksIds}
          getArbitrages={getArbitrages}
          onSelectProject={onSelectProject}
          onOpenProject={onOpenProject}
          selectedProjectId={selectedProjectId}
        />
      )}
    </>
  );
};

export { Projects };
